import { useState, useEffect } from 'react'
import { Save, X, Link } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { emailTemplateService } from '@/services/emailTemplateService'
import { EmailTemplate } from '@/services/emailService'
import { RichTextEditor } from './RichTextEditor'

interface TemplateEditorDialogProps {
  isOpen: boolean
  onClose: () => void
  template: EmailTemplate
  onSave: () => void
}

export function TemplateEditorDialog({
  isOpen,
  onClose,
  template,
  onSave
}: TemplateEditorDialogProps) {
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    subject: '',
    title: '',
    content: '',
    buttonText: '',
    buttonUrl: ''
  })

  useEffect(() => {
    if (template) {
      setFormData({
        subject: template.subject || '',
        title: template.title || '',
        content: template.content || '',
        buttonText: template.buttonText || '',
        buttonUrl: template.buttonUrl || ''
      })
    }
  }, [template])

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    if (!formData.subject.trim() || !formData.title.trim() || !formData.content.trim()) {
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Konu, başlık ve içerik alanları zorunludur.'
      })
      return
    }

    try {
      setIsLoading(true)
      await emailTemplateService.update(template.slug, {
        subject: formData.subject,
        title: formData.title,
        content: formData.content,
        buttonText: formData.buttonText || null,
        buttonUrl: formData.buttonUrl || null
      })

      toast({
        title: 'Başarılı',
        description: 'Email şablonu başarıyla güncellendi.'
      })

      onSave()
    } catch (error) {
      console.error('Şablon kaydedilirken hata:', error)
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Şablon kaydedilirken bir hata oluştu.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Şablon Düzenle: {template.name}
          </DialogTitle>
          <DialogDescription>
            Email şablonunun içeriğini düzenleyin. <code className="bg-muted px-1 rounded">{'{{firma}}'}</code> kullanarak firma adını otomatik ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Konu */}
          <div className="space-y-2">
            <Label htmlFor="subject">Email Konusu *</Label>
            <Input
              id="subject"
              placeholder="Örn: Veeq Güncelleme Bildirimi"
              value={formData.subject}
              onChange={(e) => handleChange('subject', e.target.value)}
            />
          </div>

          {/* Başlık */}
          <div className="space-y-2">
            <Label htmlFor="title">Başlık *</Label>
            <Input
              id="title"
              placeholder="Örn: Yeni Güncelleme! 🚀"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Email içinde görünecek ana başlık
            </p>
          </div>

          {/* İçerik - Rich Text Editor */}
          <div className="space-y-2">
            <Label>İçerik *</Label>
            <RichTextEditor
              content={formData.content}
              onChange={(html) => handleChange('content', html)}
              placeholder="Email içeriğinizi buraya yazın..."
            />
          </div>

          {/* Buton */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="buttonText" className="flex items-center gap-1">
                <Link className="h-4 w-4" />
                Buton Metni (Opsiyonel)
              </Label>
              <Input
                id="buttonText"
                placeholder="Örn: Detayları Gör"
                value={formData.buttonText}
                onChange={(e) => handleChange('buttonText', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="buttonUrl">Buton Linki</Label>
              <Input
                id="buttonUrl"
                placeholder="https://panel.cloudmedia.com.tr"
                value={formData.buttonUrl}
                onChange={(e) => handleChange('buttonUrl', e.target.value)}
                disabled={!formData.buttonText}
              />
            </div>
          </div>

          {/* Preview Info */}
          <div className="bg-muted p-3 rounded-lg text-sm">
            <p className="font-medium mb-1">Önizleme İpucu:</p>
            <p className="text-muted-foreground">
              Değişikliklerinizi kaydettikten sonra "Önizle" butonuna tıklayarak
              email'in nasıl görüneceğini kontrol edebilirsiniz.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            <X className="h-4 w-4 mr-1" />
            İptal
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-1" />
            {isLoading ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
